import React from "react";
import {
  useMacroCollections,
  RoomIterationProvider,
} from "../MacroCollectionContext";

type MacroRoomsRepeaterProps = {
  /** Slot for the content to repeat for each room - renders as a function component */
  item?: React.FC;
  /** Whether to show when there are no rooms */
  showWhenEmpty?: boolean;
  puck?: { dragRef?: React.Ref<any> };
};

/**
 * Repeater block that iterates over all room bookings in the reservation.
 *
 * Inside the repeater, use bindings like:
 * - {{room.RoomType}} - Room type name
 * - {{room.Nights}} - Number of nights
 * - {{room.CheckInDate}} - Check-in date
 * - {{room.CheckOutDate}} - Check-out date
 * - {{room.Price}} - Formatted price
 * - {{room.Quantity}} - Quantity (for grouped rooms)
 * - {{room.ordinal}} - 1, 2, 3...
 */
const MacroRoomsRepeaterBlock = {
  label: "Rooms Repeater",
  llmDescription: `A repeater block that iterates over all room bookings in the reservation.

IMPORTANT - SLOT DATA MODEL:
This block uses the Puck SLOT pattern (NOT DropZones). The 'item' field is a SLOT that stores
child component data INLINE within the props, NOT in a separate zones object.

Correct PuckData structure for RoomsRepeater:
{
  "type": "RoomsRepeater",
  "props": {
    "id": "rooms-repeater",
    "item": [
      { "type": "Html", "props": { "id": "html-1", "content": "<p>{{room.RoomType}}</p>" } },
      { "type": "Divider", "props": { "id": "div-1" } }
    ]
  }
}

WRONG (do NOT use zones):
{
  "type": "RoomsRepeater",
  "props": { "id": "rooms-repeater" }
}
// with separate zones["rooms-repeater:item"] - THIS IS WRONG!

Inside this repeater, you can use room-specific bindings:
  - {{room.RoomType}} - Room type name (e.g., "Superior Room")
  - {{room.Nights}} - Number of nights (integer)
  - {{room.NightsUnit}} - "night" or "nights" based on Nights value
  - {{room.CheckInDate}} - Formatted check-in date
  - {{room.CheckOutDate}} - Formatted check-out date
  - {{room.Price}} - Formatted price WITH currency (e.g., "฿21,000") - DO NOT add ฿ before this!
  - {{room.Quantity}} - Quantity for grouped rooms
  - {{room.ordinal}} - Position number (1, 2, 3...)
  - {{room.count}} - Total number of room bookings

CRITICAL: {{room.Price}} already includes ฿ symbol. Writing "฿{{room.Price}}" will produce "฿฿21,000".`,
  inline: true,
  fields: {
    item: { type: "slot" as const },
    showWhenEmpty: {
      type: "radio" as const,
      label: "Show when no rooms",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
  },
  defaultProps: {
    item: [] as any[],
    showWhenEmpty: false,
  },
  render: (props: MacroRoomsRepeaterProps) => {
    const { item: Items, showWhenEmpty = false, puck } = props;
    const collections = useMacroCollections();
    const rooms = collections.rooms;

    if (!showWhenEmpty && rooms.length === 0) {
      return null;
    }

    const ref = puck?.dragRef as any;

    return (
      <div ref={ref} style={{ display: "contents" }}>
        {rooms.map((room, index) => (
          <RoomIterationProvider
            key={index}
            room={room}
            index={index}
            count={rooms.length}
          >
            {Items && <Items />}
          </RoomIterationProvider>
        ))}
      </div>
    );
  },
};

export default MacroRoomsRepeaterBlock;
